import React from 'react'
import {clone, pullAt, isEmpty} from 'lodash'
import SelectField from 'material-ui-legacy/SelectField'
import MenuItem from 'material-ui-legacy/MenuItem'
import {
  grey500,
  deepOrange500,
  greenA700
} from 'material-ui-legacy/styles/colors'
import Paper from 'material-ui-legacy/Paper'
import Row from 'muicss/lib/react/row'
import Col from 'muicss/lib/react/col'

import errors from '../../lib/errors.js'
import parseFormula from '../../stores/parse_formula'

import SmallAddButton from '../UI/SmallAddButton'
import ValidatedTextInput from '../UI/ValidatedTextInput'
import CircularLoader from '../UI/CircularLoader'
import ReferenceValueSelect from '../ReferenceValues/ReferenceValueSelect'
import {CostFormulaSelect, RateFormulaSelect} from './FormulaSelects'
import ParsedFormulaDisplay from './ParsedFormulaDisplay'


// Inner components

const AddTokenButton = (props) => (
  <SmallAddButton style={{marginTop: '20px'}} {...props} />
)

const TokenTypeSelect = (props) => (
  <SelectField
    name='tokenType'
    floatingLabelText="Formula element"
    fullWidth={props.fullWidth || true}
    style={props.style}
    value={props.value}
    onChange={props.onChange}
    required
  >
    <MenuItem
      value='reference_value'
      primaryText='Reference value'
    />
    <MenuItem
      value='cost_formula'
      primaryText='Cost formula'
    />
    <MenuItem
      value='rate_formula'
      primaryText='Rate formula'
    />
    <MenuItem
      value='op'
      primaryText='Operator'
    />
    <MenuItem
      value='value'
      primaryText='Raw value'
    />
  </SelectField>
)

const OpSelect = (props) => (
  <SelectField
    name='op'
    floatingLabelText="Operator"
    fullWidth={props.fullWidth || true}
    style={props.style}
    value={props.value}
    onChange={props.onChange}
    required
  >
    <MenuItem
      value='*'
      primaryText='multiply'
    />
    <MenuItem
      value='+'
      primaryText='add'
    />
    <MenuItem
      value='-'
      primaryText='subtract'
    />
    <MenuItem
      value='/'
      primaryText='divide'
    />
  </SelectField>
)

const ResultDisplay = (props) => (
  <div style={{marginTop: '70px', fontSize: '20px', ...props.style}}>
    {props.empty &&
      <span>
        = <span style={{color: grey500}}>No formula</span>
      </span>
    }
    {props.formattedResult && !props.empty &&
      <span>
        = <span style={{color: greenA700}}>{props.formattedResult}</span>
      </span>
    }
    {props.error && !props.empty &&
      <span>
        = <span style={{color: deepOrange500}}>{props.error}</span>
      </span>
    }
  </div>
)

/*
Props:
- name: field name
- validatedOnChange: callback function for onChange, but including a
  validation flag and field name:
  callback(value, Bool(isValid), this.props.name)
*/
class FormulaInput extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      formula: (this.props.formula || []),
      parsedFormula: {},
      tokenType: null,
      tokenValue: null,
      valid: false,
      changed: false,
      result: null,
      formattedResult: '$0.00',
      error: '',
      loading: false
    }
  }

  componentDidMount() {
    if (this.state.formula) this._parseFormula()
  }

  _parseFormula = (formula = this.state.formula) => {
    this.setState({loading: true})
    parseFormula(formula)
    .then((result) => {
      const parsedFormula = result.data.attributes
      const valid = Boolean(parsedFormula.result)
      this.setState(
        {
          formula: formula,
          parsedFormula: result.data,
          result: parsedFormula.result,
          formattedResult: (parsedFormula.result ? parsedFormula.formattedResult : null),
          tokenType: null,
          tokenValue: null,
          valid: valid,
          changed: true,
          error: parsedFormula.error,
          loading: false
        },
        () => {
          if (this.props.validatedOnChange) {
            this.props.validatedOnChange(
              this.state.formula,
              this.state.valid,
              this.props.name
            )
          }
        }
      )
    })
    .catch((error) => this.setState({error: errors.unpack(error), loading: false}))
  }

  _handleTokenType = (ev, key, value) => {
    this.setState({tokenType: value, tokenValue: null})
  }

  _handleSelects = (key, value) => {
    this.setState({tokenValue: value})
  }

  _handleValue = (value, valid, name) => {
    this.setState({tokenValue: value})
  }

  _handleAddToken = () => {
    let updatedFormula = clone(this.state.formula)
    const tokenType = (
      this.state.tokenType.match(/formula/) ?
      'formula' :
      this.state.tokenType
    )
    let newToken = {}
    newToken[tokenType] = (
      tokenType.match(/formula|reference_value|value/) ?
      Number(this.state.tokenValue) :
      this.state.tokenValue
    )
    updatedFormula.push(newToken)
    this._parseFormula(updatedFormula)
  }

  _handleTokenDelete = (tokenIndex) => {
    let updatedFormula = clone(this.state.formula)
    pullAt(updatedFormula, tokenIndex)
    this._parseFormula(updatedFormula)
  }

  render() {
    return (
      <div>
        <Row>
          <Col lg="3" md="4" sm="6">
            <TokenTypeSelect
              value={this.state.tokenType}
              onChange={this._handleTokenType}
            />
          </Col>
          {this.state.tokenType === 'op' &&
            <Col lg="3" md="4" sm="6">
              <OpSelect
                value={this.state.tokenValue}
                onChange={(ev, key, value) => this._handleSelects('op', value)}
              />
            </Col>
          }
          {this.state.tokenType === 'reference_value' &&
            <Col lg="6" md="6" sm="11">
              <ReferenceValueSelect
                selectedId={this.state.referenceValueId}
                validatedOnChange={(value, valid, name) => this._handleSelects('referenceValueId', value)}
              />
            </Col>
          }
          {this.state.tokenType === 'cost_formula' &&
            <Col lg="6" md="6" sm="11">
              <CostFormulaSelect
                selectedId={this.state.costFormulaId}
                validatedOnChange={(value, valid, name) => this._handleSelects('costFormulaId', value)}
              />
            </Col>
          }
          {this.state.tokenType === 'rate_formula' &&
            <Col lg="6" md="6" sm="11">
              <RateFormulaSelect
                selectedId={this.state.rateFormulaId}
                validatedOnChange={(value, valid, name) => this._handleSelects('rateFormulaId', value)}
              />
            </Col>
          }
          {this.state.tokenType === 'value' &&
            <Col lg="3" md="4" sm="6">
              <ValidatedTextInput
                name='rawValue'
                value={this.state.rawValue}
                required={true}
                floatingLabelText="Raw value"
                errorMessage="Invalid value"
                pattern={/^-?(\d+)\.?(\d+)?$/}
                fullWidth={true}
                validatedOnChange={this._handleValue}
              />
            </Col>
          }
          {this.state.tokenType &&
            <Col lg="1" md="1" sm="1">
              <AddTokenButton
                onClick={this._handleAddToken}
              />
            </Col>
          }
          {this.state.loading &&
            <Col lg="1" md="1" sm="1">
              <CircularLoader size={24} style={{marginTop: '34px'}} />
            </Col>
          }
        </Row>
        <Row>
          <Col lg={10} md={9} sm={9}>
            <Paper style={{height: '200px', padding: '5px'}}>
              <ParsedFormulaDisplay
                parsedFormula={this.state.parsedFormula}
                onTokenDelete={this._handleTokenDelete}
              />
            </Paper>
          </Col>
          <Col lg={2} md={3} sm={3}>
            <ResultDisplay
              empty={isEmpty(this.state.formula)}
              formattedResult={this.state.formattedResult}
              error={this.state.error}
            />
          </Col>
        </Row>
      </div>
    )
  }
}

export default FormulaInput



// WEBPACK FOOTER //
// ./src/components/Formulas/FormulaInput.jsx